import { set } from "lodash-es"
import { action, makeAutoObservable, observable } from "mobx"

import {
  defaultActionValueProps,
  popupListProps,
  workflowAction,
  workflowInfo,
  workflowRules,
} from "@/stores/Workflow/type"
import {WorkflowType} from "@/pages/Setting/components/Automation";

export default class WorkflowStore {
  // 画布拖动的偏移量
  @observable graphPosition = {
    x: 0,
    y: 0,
  }
  // 是否点击了保存按钮和激活按钮
  @observable isWorkflowSaveBtn = false

  @observable hasTemplate = false

  // 模板类型 0 自动同意 1 自动退货
  @observable templateType?: WorkflowType = undefined;

  @observable hasApproveAction = {
    id: 0,
    status: true, // true表示可以添加
  } // 判断当前列表是否已经有action（当前的流程图只有auto-approve）

  // exchange的默认值
  // @observable hasExchangeAction = {
  //   id: 0,
  //   status: true, // true表示可以添加
  // }
  // action数据默认值（邮件地址和包裹类型）
  @observable defaultActionValue: defaultActionValueProps = {
    returnAddressId: 0,
    returnPackageId: 0,
  }
  // 基础数据信息
  @observable info: workflowInfo = {
    id: 0, // 工作流id 0是新增，大于0是修改
    name: "", // 名称
    kind: 0, // 工作流类型
    logical: 1, // 逻辑 1-and 2-or 3-无条件
    enable: 1, // 开启状态 1-草稿Draft 2-已激活active 3-关闭inactive
    isCheckRule: false, // 是否校验rule
    isCheckAction: false, // 是否校验action
  }
  // 动作数据列表
  @observable workFlowAction: workflowAction = {
    return_address_id: 0,
    package_id: 0,
    logistics: 0,
    carrier_option: 0,
  }
  // 条件数据列表
  @observable workflowRules: workflowRules[] = [
    {
      rule_type: 0,
      rule_com: 1,
      rule_value: "",
    },
  ]

  // 工作流弹窗相关信息
  @observable popupList: popupListProps = {
    workflowPopup: true, // 工作流表单弹窗
    onePopup: false,
    twoPopup: false,
    threePopup: false,
    fourPopup: false,
    fivePopup: false,
    sixPopup: false,
    sevenPopup: false,
    runTestPopup: false,
    deleteWorkflowPopup: false,
  }

  // 按钮状态
  @observable popupSaveBtn = {
    active: false,
    loading: false,
    activateLoading: false,
  }

  // 工作流初始默认值
  @observable initWorkflowOptions = {
    info: this.info, // 工作流表单弹窗
    workFlowAction: this.workFlowAction,
    workflowRules: this.workflowRules,
  }

  // 当前勾选node数据
  @observable curActiveNode = {
    index: 0,
    label: "",
    title: "",
  }

  // 校验
  @observable checkedList = {
    isCheckWorkflowName: true, // 检验工作流名称是否重复：true-可用 false-不可用
    isBlurCheckName: true, // 校验失焦工作流名称
  }

  // 流程图初始数据
  @observable flowData = {}

  // 流程画布node节点高亮与下拉框状态
  @observable flowByNodeStatus = {
    isHeightLight: { index: 0, status: false }, // node节点是否高亮
    isShowDropdown: false,
  }

  // 流程表单当前高亮hover位置
  @observable workFormByPositionActive = {
    action: false,
    isCurActiveCondition: 0,
  }

  constructor() {
    // 使用这个才会在 MobX 6 上才会更新视图
    makeAutoObservable(this)
  }

  /**
   * 设置基础数据信息
   */
  @action.bound
  setInfo<T>(path: string, val: T) {
    set(this.info, path, val)
  }

  @action.bound // 更新整个列表
  setInfoList(_info: workflowInfo) {
    this.info = _info
  }

  /**
   * 设置动作数据列表
   */
  @action.bound
  setWorkFlowAction<T>(path: string, val: T) {
    set(this.workFlowAction, path, val)
  }

  @action.bound // 更新整个列表
  setWorkFlowActionList(_workFlowActionList: workflowAction) {
    this.workFlowAction = _workFlowActionList
  }

  /**
   * 设置条件数据列表
   */
  @action.bound
  setWorkflowRules(_workflowRules: workflowRules[]) {
    this.workflowRules = _workflowRules
  }

  /**
   * 设置条件数据列表
   */
  @action.bound
  setPopupList<T>(path: string, val: T) {
    set(this.popupList, path, val)
  }

  /**
   * 设置工作流默认值
   */
  @action.bound
  setInitWorkflowOptions(info) {
    this.initWorkflowOptions = info
  }

  /**
   * 设置条件数据单个path
   */
  @action.bound
  setInitWorkflowOptionsByPath<T>(path: string, val: T) {
    set(this.initWorkflowOptions, path, val)
  }

  /**
   * 设置按钮状态
   */
  @action.bound
  setPopupSaveBtn(key: string, val: Boolean) {
    this.popupSaveBtn[key] = val
  }

  /**
   * 流程图数据结构
   */
  @action.bound
  setFlowData(info) {
    this.flowData = info
  }

  /**
   * 设置当前选择的node数据
   */
  @action.bound
  setCurActiveNode(info) {
    this.curActiveNode = info
  }

  /**
   * 设置校验数据
   */
  @action.bound
  setCheckedList(key: string, val: Boolean) {
    this.checkedList[key] = val
  }

  /**
   * 设置当前列表是否已经有action
   */
  @action.bound
  setHasApproveAction(info) {
    this.hasApproveAction = info
  }
  /**
   * 设置当前列表是否已经有action exchange
   */
  // @action.bound
  // setHasExchangeAction(info) {
  //   this.hasExchangeAction = info
  // }

  /**
   * 设置邮件地址和包裹类型的默认值
   */
  @action.bound
  setDefaultActionValue(key: string, val: number) {
    this.defaultActionValue[key] = val
  }

  /**
   * 设置流程画布node节点高亮与下拉框状态
   */
  @action.bound
  setFlowByNodeStatus(info) {
    this.flowByNodeStatus = info
  }

  /**
   * 设置当前是否流程模板
   */
  @action.bound
  setHasTemplate(val: boolean) {
    this.hasTemplate = val
  }
  /**
   * 设置当前是否模板类型
   */
  @action.bound
  setTemplate(type?: WorkflowType) {
    this.templateType = type
  }

  /**
   * 设置流程表单当前高亮hover位置(Path)
   */
  @action.bound
  setWorkFormByPositionActiveByPath<T>(key: string, val: T) {
    this.workFormByPositionActive[key] = val
  }

  /**
   * 设置流程表单当前高亮hover位置
   */
  @action.bound
  setWorkFormByPositionActive(info) {
    this.workFormByPositionActive = info
  }

  // 是否点击了保存按钮和激活按钮
  @action.bound
  setIsWorkflowSaveBtn(val) {
    this.isWorkflowSaveBtn = val
  }

  // 设置画布拖动的偏移量
  @action.bound
  setGraphPosition(val) {
    this.graphPosition = val
  }
}
